/**
 * Hook quản lý trang danh sách bất động sản (tìm kiếm + phân trang).
 *
 * Đồng bộ bộ lọc với URL, tải danh mục/địa điểm tham chiếu, fetch danh sách tin
 * có cache in-memory theo bộ filter, và điều khiển scroll tới khu vực kết quả.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { UI_MESSAGES } from '../../../shared/constants/messages';
import { hasActivePropertySearch, parsePropertySearchParams } from '../../../shared/utils/propertySearchParams';
import { DISPLAY_ONLY_SEARCH_PARAMS, buildSearchParamsFromFilters } from '../../../shared/utils/browseSectionSearch';
import { scrollToPageTop } from '../../../shared/utils/scrollToPageTop';
import { categoryApi } from '../../categories/api/categoryApi';
import { locationApi } from '../../locations/api/locationApi';
import { propertyApi } from '../api/propertyApi';
import { PROPERTY_SORTS, TRANSACTION_TYPES } from '../../../shared/constants/property';
import { waitMinimumDelay } from '../../../shared/utils/waitMinimumDelay';

const defaultSearchFilters = {
  ...parsePropertySearchParams(new URLSearchParams()),
  page: '1',
  section: '',
  source: '',
};

const buildFiltersFromSearchParams = (searchParams) => ({
  ...defaultSearchFilters,
  ...parsePropertySearchParams(searchParams),
  sort: searchParams.get('sort') || defaultSearchFilters.sort,
  page: searchParams.get('page') || '1',
  section: searchParams.get('section') || '',
  source: searchParams.get('source') || '',
});

const isDefaultSearchParam = (key, value) =>
  (key === 'page' && value === '1') ||
  (key === 'sort' && value === PROPERTY_SORTS.latest) ||
  (key === 'transaction_type' && value === TRANSACTION_TYPES.sale);

/** Loại bỏ filter rỗng và param chỉ hiển thị trước khi gọi API. */
const removeEmptyFilters = (searchFilters) =>
  Object.fromEntries(
    Object.entries(searchFilters).filter(([key, filterValue]) => {
      if (!filterValue || DISPLAY_ONLY_SEARCH_PARAMS.includes(key)) {
        return false;
      }

      return !isDefaultSearchParam(key, filterValue);
    })
  );

/** Cache kết quả API theo chuỗi JSON của bộ filter đang submit. */
const propertyListCache = new Map();

/**
 * Hook danh sách BĐS: state filter, fetch có cache, đồng bộ URL và phân trang.
 *
 * @returns {Object} propertyList, pagination, searchFilters, handlers tìm kiếm/phân trang, ...
 */
export const usePropertyList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestIdRef = useRef(0);
  const [searchFilters, setSearchFilters] = useState(() => buildFiltersFromSearchParams(searchParams));
  const [submittedSearchFilters, setSubmittedSearchFilters] = useState(() =>
    buildFiltersFromSearchParams(searchParams)
  );
  const [propertyList, setPropertyList] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [locationTree, setLocationTree] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isFetchingProperties, setIsFetchingProperties] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [shouldScrollToResults, setShouldScrollToResults] = useState(false);
  const hasExistingResultsRef = useRef(false);

  const activeSubmittedFilters = useMemo(
    () => removeEmptyFilters(submittedSearchFilters),
    [submittedSearchFilters]
  );
  const requestCacheKey = useMemo(
    () => JSON.stringify(activeSubmittedFilters),
    [activeSubmittedFilters]
  );

  // Tải danh mục và cây địa điểm một lần khi mount
  useEffect(() => {
    let isMounted = true;

    Promise.all([categoryApi.getCategories(), locationApi.getLocations()]).then(
      ([categoryResponse, locationResponse]) => {
        if (isMounted) {
          setCategoryList(categoryResponse.data.data);
          setLocationTree(locationResponse.data.data);
        }
      }
    );

    return () => {
      isMounted = false;
    };
  }, []);

  // Khi URL đổi (back/forward hoặc link): đồng bộ state filter và cờ scroll
  useEffect(() => {
    const nextFilters = buildFiltersFromSearchParams(searchParams);

    setSearchFilters(nextFilters);
    setSubmittedSearchFilters(nextFilters);

    if (hasActivePropertySearch(searchParams)) {
      setShouldScrollToResults(true);
    } else {
      setShouldScrollToResults(false);
    }
  }, [searchParams]);

  // Fetch danh sách tin theo filter đã submit; hủy request cũ bằng requestId
  useEffect(() => {
    let isMounted = true;
    const currentRequestId = requestIdRef.current + 1;
    requestIdRef.current = currentRequestId;
    const fetchStartedAt = Date.now();

    setIsFetchingProperties(true);
    setErrorMessage('');

    let responseSnapshot = null;

    const applyResponse = (response) => {
      setPropertyList(response.data.data);
      setPagination(response.data.pagination);
      hasExistingResultsRef.current = response.data.data.length > 0;
      propertyListCache.set(requestCacheKey, {
        propertyList: response.data.data,
        pagination: response.data.pagination,
      });
    };

    const finishFetch = () => {
      if (!isMounted || requestIdRef.current !== currentRequestId) {
        return;
      }

      if (responseSnapshot) {
        applyResponse(responseSnapshot);
      }

      setIsFetchingProperties(false);
      setIsInitialLoading(false);
    };

    const cachedResponse = propertyListCache.get(requestCacheKey);
    const requestPromise = cachedResponse
      ? Promise.resolve({
          data: {
            data: cachedResponse.propertyList,
            pagination: cachedResponse.pagination,
          },
        })
      : propertyApi.getProperties(activeSubmittedFilters);

    requestPromise
      .then((response) => {
        if (!isMounted || requestIdRef.current !== currentRequestId) {
          return;
        }

        responseSnapshot = response;
      })
      .catch(() => {
        if (isMounted && requestIdRef.current === currentRequestId) {
          setErrorMessage(UI_MESSAGES.propertyLoadError);
        }
      })
      .finally(() => {
        // Giữ loading tối thiểu để tránh nhấp nháy UI
        waitMinimumDelay(fetchStartedAt).then(() => {
          finishFetch();
        });
      });

    return () => {
      isMounted = false;
    };
  }, [activeSubmittedFilters, reloadKey, requestCacheKey]);

  const syncSearchParams = useCallback((filters) => {
    setSearchParams(buildSearchParamsFromFilters(filters), { replace: true });
  }, [setSearchParams]);

  const updateSearchFilter = (filterName, filterValue) => {
    setSearchFilters((currentSearchFilters) => ({
      ...currentSearchFilters,
      [filterName]: filterValue,
      page: '1',
    }));
  };

  const patchSearchFilters = (updates) => {
    setSearchFilters((currentSearchFilters) => ({
      ...currentSearchFilters,
      ...updates,
      page: '1',
    }));
  };

  /** Áp dụng filter (submit tìm kiếm): cập nhật URL và trigger fetch qua submittedSearchFilters. */
  const applySearchFilters = (overrideFilters) => {
    const nextFilters = {
      ...(overrideFilters || searchFilters),
      page: '1',
    };

    if (overrideFilters) {
      setSearchFilters(nextFilters);
    }

    setSubmittedSearchFilters(nextFilters);
    syncSearchParams(nextFilters);
    setShouldScrollToResults(true);
  };

  const resetSearchFilters = () => {
    setSearchParams({});
    setSearchFilters(defaultSearchFilters);
    setSubmittedSearchFilters(defaultSearchFilters);
    setShouldScrollToResults(false);
  };

  const changePage = (page) => {
    scrollToPageTop();

    const nextFilters = {
      ...submittedSearchFilters,
      page: String(page),
    };

    syncSearchParams(nextFilters);
    setShouldScrollToResults(true);
  };

  const clearScrollRequest = useCallback(() => {
    setShouldScrollToResults(false);
  }, []);

  /** Xóa cache entry hiện tại và buộc fetch lại (retry sau lỗi). */
  const retryLoadProperties = () => {
    propertyListCache.delete(requestCacheKey);
    setReloadKey((currentReloadKey) => currentReloadKey + 1);
  };

  return {
    propertyList,
    categoryList,
    locationTree,
    pagination,
    searchFilters,
    isInitialLoading,
    isFetchingProperties,
    errorMessage,
    shouldScrollToResults,
    updateSearchFilter,
    patchSearchFilters,
    applySearchFilters,
    resetSearchFilters,
    changePage,
    clearScrollRequest,
    retryLoadProperties,
  };
};
